import { config } from "@/lib/config";
import { log } from "@soulbyte/core";
import { useCallback, useSyncExternalStore } from "react";

const eventName = `${config.key}.threads.pinned.changed`;
const storageKey = `${config.key}.threads.pinned`;

// Cache to store Set instances per assistantId
const cache = new Map<string, { raw: string; set: Set<string> }>();

async function safeGetStorageItem(key: string): Promise<string | null> {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    log.error(error, { extra: { key }, disableTracking: true });
    return null;
  }
}

async function safeSetStorageItem(
  key: string,
  value: string,
): Promise<boolean> {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    log.error(error, { extra: { key, value }, disableTracking: true });
    return false;
  }
}

function safeJsonParse<T>(json: string, fallback: T): T {
  try {
    return JSON.parse(json) as T;
  } catch (error) {
    log.error(error, { extra: { json, fallback }, disableTracking: true });
    return fallback;
  }
}

// Synchronous version for useSyncExternalStore - uses cached value
let cachedStorageValue: string | null = null;

async function loadStorageValue(): Promise<void> {
  cachedStorageValue = await safeGetStorageItem(storageKey);
}

// Initialize cache on module load
loadStorageValue();

function getPinnedIds(assistantId?: string): Set<string> {
  if (!assistantId) {
    return new Set<string>();
  }

  const stored = cachedStorageValue;
  const raw = stored ?? "";

  const cached = cache.get(assistantId);
  if (cached && cached.raw === raw) {
    return cached.set;
  }

  const parsed = stored
    ? safeJsonParse<Record<string, string[]>>(stored, {})
    : {};
  const assistantPins = parsed[assistantId];
  const set = new Set(Array.isArray(assistantPins) ? assistantPins : []);
  cache.set(assistantId, { raw, set });

  return set;
}

function subscribe(callback: () => void) {
  window.addEventListener(eventName, callback);
  return () => window.removeEventListener(eventName, callback);
}

const emptySet = new Set<string>();

export function usePinnedThreadIds(assistantId?: string) {
  const getSnapshot = useCallback(
    () => getPinnedIds(assistantId),
    [assistantId],
  );

  return useSyncExternalStore(subscribe, getSnapshot, () => emptySet);
}

export function useToggleThreadPin(assistantId?: string) {
  return useCallback(
    async (threadId: string) => {
      if (!assistantId) {
        return;
      }

      const stored = await safeGetStorageItem(storageKey);
      const allPinned = stored
        ? safeJsonParse<Record<string, string[]>>(stored, {})
        : {};

      const currentPinned = Array.isArray(allPinned[assistantId])
        ? allPinned[assistantId]
        : [];
      const isPinned = currentPinned.includes(threadId);
      const updated = isPinned
        ? currentPinned.filter((id) => id !== threadId)
        : [...currentPinned, threadId];

      allPinned[assistantId] = updated;

      const success = await safeSetStorageItem(
        storageKey,
        JSON.stringify(allPinned),
      );
      if (!success) {
        return;
      }

      // Update cache and dispatch custom event to trigger re-render
      cachedStorageValue = JSON.stringify(allPinned);
      window.dispatchEvent(new Event(eventName));
    },
    [assistantId],
  );
}

export async function removePinnedThread(
  threadId: string,
  assistantId: string,
) {
  const stored = await safeGetStorageItem(storageKey);
  const allPinned = stored
    ? safeJsonParse<Record<string, string[]>>(stored, {})
    : {};

  const currentPinned = Array.isArray(allPinned[assistantId])
    ? allPinned[assistantId]
    : [];

  // Only update if the thread is actually pinned
  if (!currentPinned.includes(threadId)) {
    return;
  }

  const updated = currentPinned.filter((id) => id !== threadId);
  allPinned[assistantId] = updated;

  const success = await safeSetStorageItem(
    storageKey,
    JSON.stringify(allPinned),
  );
  if (!success) {
    return;
  }

  // Update cache and dispatch custom event to trigger re-render
  cachedStorageValue = JSON.stringify(allPinned);
  window.dispatchEvent(new Event(eventName));
}
